import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Download, Film, ImageIcon } from 'lucide-react';
import { visualKitApi, type Motion, type VisualFormat, type VisualRequest } from '../../api/visualKit';
import { Alert } from '../../components/ui/Alert';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { Input, Select, Textarea } from '../../components/ui/Input';
import { SegmentedControl } from '../../components/ui/Tabs';
import { Spinner } from '../../components/ui/Spinner';

/**
 * Marketing → Visuals: Lea's SEQUESTO Live kit as a form. Pick a frame, size and
 * background, type the copy, and the API renders it (PNG, or MP4 with motion).
 * The API checks Lea's rules; anything it refuses is shown here as it was said.
 */

const FORMAT_LABELS: Record<VisualFormat, string> = {
  square: 'Square · 1080 × 1080',
  portrait: 'Portrait 4:5 · 1080 × 1350',
  landscape: '16:9 · 1920 × 1080',
  og: 'Share image · 1200 × 630',
  'form-header': 'LinkedIn form header · 552 × 200',
};

const SECONDS = [4, 6, 8, 10, 12];

interface Speaker {
  name: string;
  role: string;
  photo: { base64: string; mimeType: string; fileName: string } | null;
}

/** Lea's title card, so the page opens on a real example rather than an empty form. */
const EXAMPLE = {
  eyebrow: 'Live session',
  headline: 'Clarification Questions\nas *Strategy*',
  supporting: 'Jo Hillman in conversation with Patrick Dalvinck',
  dateDay: 'Thursday 29 October',
  dateTime: '14:00 CET  |  13:00 GMT',
};

function readAsBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result).split(',')[1] ?? '');
    reader.onerror = () => reject(reader.error ?? new Error('Could not read the photo'));
    reader.readAsDataURL(file);
  });
}

function saveBlob(blob: Blob, fileName: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  a.click();
  URL.revokeObjectURL(url);
}

export function VisualsView() {
  const catalog = useQuery({ queryKey: ['visual-kit', 'catalog'], queryFn: visualKitApi.catalog, staleTime: Infinity });

  const [frameId, setFrameId] = useState('ad-title-card');
  const [format, setFormat] = useState<VisualFormat>('square');
  const [background, setBackground] = useState('texture_umber');
  const [eyebrow, setEyebrow] = useState(EXAMPLE.eyebrow);
  const [headline, setHeadline] = useState(EXAMPLE.headline);
  const [supporting, setSupporting] = useState(EXAMPLE.supporting);
  const [dateDay, setDateDay] = useState(EXAMPLE.dateDay);
  const [dateTime, setDateTime] = useState(EXAMPLE.dateTime);
  const [label, setLabel] = useState('Live');
  const [speaker, setSpeaker] = useState<Speaker>({ name: '', role: '', photo: null });

  const [still, setStill] = useState<{ url: string; blob: Blob; fileName: string } | null>(null);
  const [stillError, setStillError] = useState<string | null>(null);
  const [rendering, setRendering] = useState(false);

  const [motion, setMotion] = useState<Motion>('build');
  const [seconds, setSeconds] = useState(6);
  const [video, setVideo] = useState<{ url: string; blob: Blob; fileName: string } | null>(null);
  const [videoError, setVideoError] = useState<string | null>(null);
  const [animating, setAnimating] = useState(false);

  const frames = catalog.data?.frames ?? [];
  const frame = frames.find((f) => f.id === frameId);
  const hasText = frame?.layout !== 'background';
  const isCover = frame?.layout === 'cover';

  const backgrounds = useMemo(
    () =>
      (catalog.data?.backgrounds ?? []).filter(
        (b) =>
          b.formats.includes(format) &&
          (!b.onlyFrames || b.onlyFrames.includes(frameId)) &&
          (!frame?.tone || b.tone === frame.tone),
      ),
    [catalog.data, format, frameId, frame?.tone],
  );

  // A frame brings its own default size and background, as Lea set them.
  const chooseFrame = (id: string) => {
    const next = frames.find((f) => f.id === id);
    setFrameId(id);
    if (next) {
      setFormat(next.defaultFormat);
      setBackground(next.defaultBackground);
    }
  };

  // Keep the background valid when the size changes.
  useEffect(() => {
    if (backgrounds.length && !backgrounds.some((b) => b.id === background)) setBackground(backgrounds[0]!.id);
  }, [backgrounds, background]);

  const request: VisualRequest | null = useMemo(() => {
    if (!frame) return null;
    if (!hasText) return { frame: frameId, format, background, content: {} };
    const date = isCover
      ? [dateDay, ...dateTime.split('|')].map((s) => s.trim()).filter(Boolean)
      : [dateDay, dateTime].filter((s) => s.trim());
    return {
      frame: frameId,
      format,
      background,
      content: {
        eyebrow: eyebrow || undefined,
        headline,
        supporting: supporting || undefined,
        date,
        ...(isCover ? { label } : {}),
        ...(isCover && speaker.photo && speaker.name.trim()
          ? {
              speaker: {
                name: speaker.name,
                role: speaker.role || undefined,
                photoBase64: speaker.photo.base64,
                photoMimeType: speaker.photo.mimeType,
              },
            }
          : {}),
      },
    };
  }, [frame, hasText, isCover, frameId, format, background, eyebrow, headline, supporting, dateDay, dateTime, label, speaker]);

  // Re-render the preview shortly after the last change; drop answers to older requests.
  const latest = useRef(0);
  useEffect(() => {
    if (!request) return;
    const id = ++latest.current;
    const timer = setTimeout(async () => {
      setRendering(true);
      try {
        const { blob, fileName } = await visualKitApi.render(request);
        if (id !== latest.current) return;
        setStill((prev) => {
          if (prev) URL.revokeObjectURL(prev.url);
          return { url: URL.createObjectURL(blob), blob, fileName: fileName ?? `${request.frame}.png` };
        });
        setStillError(null);
      } catch (error) {
        if (id === latest.current) setStillError(error instanceof Error ? error.message : String(error));
      } finally {
        if (id === latest.current) setRendering(false);
      }
    }, 700);
    return () => clearTimeout(timer);
  }, [request]);

  // A video made from older copy no longer matches the form.
  useEffect(() => {
    setVideo((prev) => {
      if (prev) URL.revokeObjectURL(prev.url);
      return null;
    });
  }, [request, motion, seconds]);

  const canAnimate = catalog.data?.animation.formats.includes(format) ?? false;
  const motions = (catalog.data?.motions ?? []).filter((m) => hasText || m.id === 'push');
  useEffect(() => {
    if (!hasText) setMotion('push');
  }, [hasText]);

  const makeVideo = async () => {
    if (!request) return;
    setAnimating(true);
    setVideoError(null);
    try {
      const { blob, fileName } = await visualKitApi.animate({ ...request, motion, seconds });
      setVideo({ url: URL.createObjectURL(blob), blob, fileName: fileName ?? `${request.frame}.mp4` });
    } catch (error) {
      setVideoError(error instanceof Error ? error.message : String(error));
    } finally {
      setAnimating(false);
    }
  };

  if (catalog.isLoading) {
    return (
      <div className="flex justify-center py-16">
        <Spinner />
      </div>
    );
  }
  if (catalog.error) {
    return (
      <Alert variant="error" title="The visual kit did not load">
        {catalog.error instanceof Error ? catalog.error.message : String(catalog.error)}
      </Alert>
    );
  }

  const size = catalog.data?.formats.find((f) => f.id === format);

  return (
    <div className="grid gap-6 lg:grid-cols-[minmax(0,380px)_minmax(0,1fr)]">
      <Card padding="md" className="flex flex-col gap-4 self-start">
        <Select label="Frame" value={frameId} onChange={(e) => chooseFrame(e.target.value)}>
          {frames.map((f) => (
            <option key={f.id} value={f.id}>
              {f.label}
            </option>
          ))}
        </Select>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-1">
          <Select label="Size" value={format} onChange={(e) => setFormat(e.target.value as VisualFormat)}>
            {(frame?.formats ?? []).map((f) => (
              <option key={f} value={f}>
                {FORMAT_LABELS[f]}
              </option>
            ))}
          </Select>
          <Select label="Background" value={background} onChange={(e) => setBackground(e.target.value)}>
            {backgrounds.map((b) => (
              <option key={b.id} value={b.id}>
                {b.label}
              </option>
            ))}
          </Select>
        </div>

        {hasText && (
          <>
            <Input id="vk-eyebrow" label="Eyebrow" value={eyebrow} onChange={(e) => setEyebrow(e.target.value)} hint="The format: Live masterclass, Live session…" />
            <Textarea
              id="vk-headline"
              label="Headline"
              rows={3}
              value={headline}
              onChange={(e) => setHeadline(e.target.value)}
              hint="Put exactly one word in *asterisks* to set it in italic. A new line forces a break."
            />
            <Textarea id="vk-supporting" label="Supporting line" rows={2} value={supporting} onChange={(e) => setSupporting(e.target.value)} hint="Optional." />
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-1">
              <Input id="vk-date-day" label="Date" value={dateDay} onChange={(e) => setDateDay(e.target.value)} />
              <Input
                id="vk-date-time"
                label="Time"
                value={dateTime}
                onChange={(e) => setDateTime(e.target.value)}
                hint={isCover ? 'Separate time zones with | ; covers join them with ·' : undefined}
              />
            </div>
          </>
        )}

        {isCover && (
          <>
            <Input id="vk-label" label="Label after the logo" value={label} onChange={(e) => setLabel(e.target.value)} hint="Leave empty to drop the divider." />
            <div className="flex flex-col gap-3 border-t border-border-light pt-4">
              <p className="text-body-sm font-medium text-text-primary">
                Speaker{frame?.speaker === 'required' ? '' : ' (optional)'}
              </p>
              <Input id="vk-speaker-name" label="Name" value={speaker.name} onChange={(e) => setSpeaker({ ...speaker, name: e.target.value })} />
              <Input id="vk-speaker-role" label="Role" value={speaker.role} onChange={(e) => setSpeaker({ ...speaker, role: e.target.value })} />
              <Input
                id="vk-speaker-photo"
                label="Photo"
                type="file"
                accept="image/jpeg,image/png,image/webp"
                hint={speaker.photo ? speaker.photo.fileName : 'A square headshot works best; it is drawn round.'}
                onChange={async (e) => {
                  const file = e.target.files?.[0];
                  if (!file) return;
                  setSpeaker({ ...speaker, photo: { base64: await readAsBase64(file), mimeType: file.type, fileName: file.name } });
                }}
              />
            </div>
          </>
        )}
      </Card>

      <div className="flex min-w-0 flex-col gap-4">
        <Card padding="md" className="flex flex-col gap-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="text-body-sm text-text-secondary">
              {size ? `${size.width} × ${size.height} px` : ''}
              {rendering ? ' · updating…' : ''}
            </p>
            <Button
              variant="secondary"
              size="sm"
              leftIcon={<Download className="h-4 w-4" />}
              disabled={!still || Boolean(stillError)}
              onClick={() => still && saveBlob(still.blob, still.fileName)}
            >
              Download PNG
            </Button>
          </div>
          {stillError && (
            <Alert variant="error" title="This visual breaks the kit rules">
              <span className="whitespace-pre-line">{stillError}</span>
            </Alert>
          )}
          <div className="flex justify-center rounded border border-border-light bg-surface-secondary p-3">
            {still ? (
              <img
                src={still.url}
                alt="Preview of the visual"
                className={`max-h-[70vh] w-auto max-w-full ${stillError ? 'opacity-40' : ''}`}
              />
            ) : (
              <div className="flex h-64 items-center gap-2 text-body-sm text-text-tertiary">
                <ImageIcon className="h-4 w-4" /> Rendering the first preview…
              </div>
            )}
          </div>
        </Card>

        <Card padding="md" className="flex flex-col gap-4">
          <div className="flex flex-wrap items-center gap-3">
            <p className="text-body-sm font-medium text-text-primary">Animate</p>
            <SegmentedControl options={motions} value={motion} onChange={setMotion} ariaLabel="Motion" />
            <Select size="sm" aria-label="Length" value={seconds} onChange={(e) => setSeconds(Number(e.target.value))}>
              {SECONDS.map((s) => (
                <option key={s} value={s}>
                  {s} seconds
                </option>
              ))}
            </Select>
            <Button size="sm" leftIcon={<Film className="h-4 w-4" />} isLoading={animating} disabled={!canAnimate || Boolean(stillError)} onClick={makeVideo}>
              Make MP4
            </Button>
            {video && (
              <Button variant="secondary" size="sm" leftIcon={<Download className="h-4 w-4" />} onClick={() => saveBlob(video.blob, video.fileName)}>
                Download MP4
              </Button>
            )}
          </div>
          {!canAnimate && (
            <p className="text-body-sm text-text-tertiary">Square, 4:5 and 16:9 visuals can be animated; share images and form headers stay still.</p>
          )}
          {animating && <p className="text-body-sm text-text-tertiary">Rendering {seconds * 30} frames. This takes about as long as the video itself.</p>}
          {videoError && (
            <Alert variant="error" title="The animation could not be made">
              <span className="whitespace-pre-line">{videoError}</span>
            </Alert>
          )}
          {video && (
            <div className="flex justify-center rounded border border-border-light bg-surface-secondary p-3">
              <video src={video.url} controls autoPlay muted loop playsInline className="max-h-[70vh] w-auto max-w-full" />
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
